import { motion } from "framer-motion";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm() {
  const { t, locale } = useI18n();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    building_type: "",
    area: "",
    frequency: "",
    services: [],
    address: "",
    message: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote`, { ...form, locale });
      toast.success(t.quote.success);
      setForm({
        name: "",
        email: "",
        phone: "",
        company: "",
        building_type: "",
        area: "",
        frequency: "",
        services: [],
        address: "",
        message: "",
      });
    } catch (err) {
      toast.error(t.quote.error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#27272A] focus:border-[#E1FF00] px-4 py-3.5 text-[#F4F4F5] placeholder:text-[#52525B] font-normal transition-colors";
  const labelCls =
    "block font-mono-acc text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] mb-2";

  return (
    <section id="quote" className="relative py-24 md:py-32 bg-[#050505]" data-testid="quote-section">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="grid md:grid-cols-12 gap-8 mb-14"
        >
          <div className="md:col-span-4">
            <div className="flex items-center gap-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA]">
              <span className="w-8 h-px bg-[#E1FF00]" />
              {t.quote.overline}
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl text-[#F4F4F5] tracking-tighter leading-[0.95]">
              {t.quote.title}
            </h2>
            <p className="mt-6 text-[#A1A1AA] text-base md:text-lg max-w-xl">
              {t.quote.subtitle}
            </p>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          className="border border-[#27272A] bg-[#0a0a0a] p-6 md:p-10 grid md:grid-cols-2 gap-6"
          data-testid="quote-form"
        >
          <div>
            <label className={labelCls}>{t.quote.name} *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="quote-name" />
          </div>
          <div>
            <label className={labelCls}>{t.quote.company}</label>
            <input value={form.company} onChange={(e) => set("company", e.target.value)} className={inputCls} data-testid="quote-company" />
          </div>
          <div>
            <label className={labelCls}>{t.quote.email} *</label>
            <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="quote-email" />
          </div>
          <div>
            <label className={labelCls}>{t.quote.phone} *</label>
            <input required value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} data-testid="quote-phone" />
          </div>

          <div>
            <label className={labelCls}>{t.quote.buildingType} *</label>
            <select required value={form.building_type} onChange={(e) => set("building_type", e.target.value)} className={inputCls} data-testid="quote-building-type">
              <option value="" className="bg-[#0a0a0a]">—</option>
              {t.quote.buildingOptions.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#0a0a0a]">{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t.quote.frequency}</label>
            <select value={form.frequency} onChange={(e) => set("frequency", e.target.value)} className={inputCls} data-testid="quote-frequency">
              <option value="" className="bg-[#0a0a0a]">—</option>
              {t.quote.frequencyOptions.map((o) => (
                <option key={o.value} value={o.value} className="bg-[#0a0a0a]">{o.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className={labelCls}>{t.quote.area}</label>
            <input value={form.area} onChange={(e) => set("area", e.target.value)} className={inputCls} data-testid="quote-area" placeholder="e.g. 25000 pi²" />
          </div>
          <div>
            <label className={labelCls}>{t.quote.address}</label>
            <input value={form.address} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="quote-address" />
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>{t.quote.services}</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {t.quote.servicesOptions.map((s) => {
                const active = form.services.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleService(s)}
                    className={`px-3 py-2 border font-mono-acc text-[10px] tracking-[0.15em] uppercase transition-colors ${
                      active
                        ? "bg-[#E1FF00] text-[#050505] border-[#E1FF00]"
                        : "border-[#27272A] text-[#A1A1AA] hover:border-[#E1FF00] hover:text-[#F4F4F5]"
                    }`}
                    data-testid={`quote-service-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className={labelCls}>{t.quote.message} *</label>
            <textarea required rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls + " resize-none"} data-testid="quote-message" />
          </div>

          <div className="md:col-span-2 flex justify-end pt-2">
            <button
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-[#E1FF00] hover:bg-[#CBE600] disabled:opacity-50 text-[#050505] px-6 py-4 font-mono-acc text-[11px] tracking-[0.15em] uppercase font-semibold transition-colors"
              data-testid="quote-submit"
            >
              {loading ? t.quote.submitting : t.quote.submit}
              <span className="inline-block group-hover:translate-x-1 transition-transform">→</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
